import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeaseCreateInput,
  LeaseStatus,
  LeaseTenantRole,
  LeaseTermType,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Leases — the admin lease list + a create form (the LeaseDetail page manages tenants, the
 * payment schedule, and the ledger). Mirrors the Tenants.tsx CRUD pattern: a react-query list,
 * a "New lease" button opening an accessible {@link Dialog}, all writes via the shared `api`
 * client (CSRF handled there), invalidating the list query on success. Each row links to its
 * detail page. Money is entered in DOLLARS and converted to integer CENTS on submit.
 */

const LEASES_KEY = ['admin', 'leases'] as const;

const LEASE_STATUSES: LeaseStatus[] = ['draft', 'pending_move_in', 'active', 'ended', 'terminated'];
const TERM_TYPES: LeaseTermType[] = ['fixed', 'month_to_month'];
const TENANT_ROLES: LeaseTenantRole[] = ['primary', 'co_tenant', 'guarantor', 'occupant'];

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Parse a dollar string to integer cents; null when blank/NaN/negative. */
function dollarsToCents(v: string): number | null {
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** A local editable tenant row (attached to the lease on create). */
interface TenantRow {
  tenantId: string;
  role: LeaseTenantRole;
  isServiceMember: boolean;
}

function LeaseForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  // Unit picker: pick a property, then a unit within it.
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');

  const [status, setStatus] = useState<LeaseStatus>('draft');
  const [termType, setTermType] = useState<LeaseTermType>('fixed');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rent, setRent] = useState('');
  const [deposit, setDeposit] = useState('');
  const [exemptionNoticeInLease, setExemptionNoticeInLease] = useState(false);
  const [notes, setNotes] = useState('');
  const [tenantRows, setTenantRows] = useState<TenantRow[]>([
    { tenantId: '', role: 'primary', isServiceMember: false },
  ]);

  const propertiesQuery = useQuery({
    queryKey: ['admin', 'properties'],
    queryFn: () => api.properties.list(),
  });
  const tenantsQuery = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api.tenants.list() });
  const unitsQuery = useQuery({
    queryKey: ['admin', 'units', propertyId],
    queryFn: () => api.units.list(propertyId),
    enabled: propertyId !== '',
  });

  const validRows = tenantRows.filter((r) => r.tenantId !== '');

  const save = useMutation({
    mutationFn: () => {
      const rentCents = dollarsToCents(rent);
      if (rentCents == null) throw new Error('Enter a rent amount in dollars.');
      let depositCents: number | null = null;
      if (deposit.trim() !== '') {
        const c = dollarsToCents(deposit);
        if (c == null) throw new Error('Deposit must be a number.');
        depositCents = c;
      }
      const body: LeaseCreateInput = {
        unitId,
        status,
        termType,
        startDate,
        endDate: opt(endDate) ?? null,
        rentCents,
        depositCents,
        paymentFrequency: 'monthly',
        exemptionNoticeInLease,
        notes: opt(notes) ?? null,
        tenants: validRows.map((r) => ({
          tenantId: r.tenantId,
          role: r.role,
          isServiceMember: r.isServiceMember,
        })),
      };
      return api.leases.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEASES_KEY });
      onClose();
    },
  });

  function updateRow(index: number, patch: Partial<TenantRow>) {
    setTenantRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setTenantRows((rows) => [...rows, { tenantId: '', role: 'co_tenant', isServiceMember: false }]);
  }
  function removeRow(index: number) {
    setTenantRows((rows) => rows.filter((_, i) => i !== index));
  }

  const properties = propertiesQuery.data?.properties ?? [];
  const units = unitsQuery.data?.units ?? [];
  const tenants = tenantsQuery.data?.tenants ?? [];

  const canSubmit =
    !save.isPending && unitId !== '' && startDate !== '' && rent.trim() !== '' && validRows.length > 0;

  return (
    <Dialog title="New lease" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}

        <div>
          <label htmlFor="l-property">Property</label>
          <select
            id="l-property"
            value={propertyId}
            required
            onChange={(e) => {
              setPropertyId(e.target.value);
              setUnitId('');
            }}
          >
            <option value="">Select a property…</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="l-unit">Unit</label>
          <select
            id="l-unit"
            value={unitId}
            required
            disabled={propertyId === '' || unitsQuery.isLoading}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">
              {propertyId === '' ? 'Choose a property first' : 'Select a unit…'}
            </option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="l-status">Status</label>
          <select
            id="l-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as LeaseStatus)}
          >
            {LEASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="l-term">Term type</label>
          <select
            id="l-term"
            value={termType}
            onChange={(e) => setTermType(e.target.value as LeaseTermType)}
          >
            {TERM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'fixed' ? 'Fixed' : 'Month-to-month'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="l-start">Start date</label>
          <input
            id="l-start"
            type="date"
            value={startDate}
            required
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="l-end">End date (required for a fixed term)</label>
          <input
            id="l-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="l-rent">Monthly rent (USD)</label>
          <input
            id="l-rent"
            type="number"
            min="0"
            step="0.01"
            value={rent}
            required
            onChange={(e) => setRent(e.target.value)}
            placeholder="e.g. 2500.00"
          />
        </div>

        <div>
          <label htmlFor="l-deposit">Security deposit (USD, optional)</label>
          <input
            id="l-deposit"
            type="number"
            min="0"
            step="0.01"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
            placeholder="e.g. 2500.00"
          />
        </div>

        <div className="row">
          <input
            id="l-exemption"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={exemptionNoticeInLease}
            onChange={(e) => setExemptionNoticeInLease(e.target.checked)}
          />
          <label htmlFor="l-exemption" style={{ margin: 0 }}>
            AB 1482 exemption notice is included in this lease
          </label>
        </div>

        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend>Tenants</legend>
          <p className="muted" style={{ margin: 0 }}>
            Attach at least one tenant. Add them under Tenants first if they are not listed.
          </p>
          {tenantRows.map((row, i) => (
            <div className="row" key={i} style={{ alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor={`l-tenant-${i}`}>Tenant</label>
                <select
                  id={`l-tenant-${i}`}
                  value={row.tenantId}
                  onChange={(e) => updateRow(i, { tenantId: e.target.value })}
                >
                  <option value="">Select a tenant…</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.firstName} {t.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`l-role-${i}`}>Role</label>
                <select
                  id={`l-role-${i}`}
                  value={row.role}
                  onChange={(e) => updateRow(i, { role: e.target.value as LeaseTenantRole })}
                >
                  {TENANT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row">
                <input
                  id={`l-sm-${i}`}
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={row.isServiceMember}
                  onChange={(e) => updateRow(i, { isServiceMember: e.target.checked })}
                />
                <label htmlFor={`l-sm-${i}`} style={{ margin: 0 }}>
                  Service member
                </label>
              </div>
              <button
                type="button"
                className="secondary"
                disabled={tenantRows.length === 1}
                onClick={() => removeRow(i)}
              >
                Remove<span className="visually-hidden"> tenant row {i + 1}</span>
              </button>
            </div>
          ))}
          <div>
            <button type="button" className="secondary" onClick={addRow}>
              Add tenant row
            </button>
          </div>
        </fieldset>

        <div>
          <label htmlFor="l-notes">Notes (optional)</label>
          <textarea
            id="l-notes"
            value={notes}
            rows={3}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="row">
          <button type="submit" disabled={!canSubmit}>
            {save.isPending ? 'Creating…' : 'Create lease'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function AdminLeases() {
  const query = useQuery({ queryKey: LEASES_KEY, queryFn: () => api.leases.list() });
  const [creating, setCreating] = useState(false);

  if (query.isLoading) return <Loading label="Loading leases…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const leases = query.data.leases;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Leases</h1>
        <button type="button" onClick={() => setCreating(true)}>
          New lease
        </button>
      </div>
      <p className="subtitle">All leases across the portfolio. Open one to manage tenants, schedule, and ledger.</p>

      {leases.length === 0 ? (
        <p className="muted">No leases yet. Choose “New lease” to add one.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Leases</caption>
            <thead>
              <tr>
                <th scope="col">Unit</th>
                <th scope="col">Status</th>
                <th scope="col">Term</th>
                <th scope="col" className="num">
                  Rent
                </th>
                <th scope="col">Tenants</th>
              </tr>
            </thead>
            <tbody>
              {leases.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={`/admin/leases/${l.id}`}>{l.unitLabel ?? l.unitId}</Link>
                  </td>
                  <td>
                    <span className="badge">{l.status}</span>
                  </td>
                  <td>
                    {l.termType === 'fixed' ? 'Fixed' : 'MTM'}
                    <span className="visually-hidden">
                      {' '}
                      from {formatDate(l.startDate)}
                      {l.endDate ? ` to ${formatDate(l.endDate)}` : ''}
                    </span>
                  </td>
                  <td className="num">{formatCents(l.rentCents)}</td>
                  <td>
                    {l.tenants
                      ?.map((t) => (t.tenant ? `${t.tenant.firstName} ${t.tenant.lastName}` : t.tenantId))
                      .join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <LeaseForm onClose={() => setCreating(false)} />}
    </>
  );
}
